import { useEffect, useState } from "react";
import { COMMISSION_BASIS_LABEL, ROLE_LABEL, type Role, type SalesSettings } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";

// Team → Commissions: the company's own plan. Nothing here is fixed by
// Clevis: what it's figured on, when it's earned, and a default rate per
// employee type. A person's own rate (People → open the person) wins over
// their type's default. Payroll and Reports → Commissions use these numbers.

const TYPES: Role[] = ["tech", "staff", "manager", "admin", "owner"];
const drafts = (s: SalesSettings) => Object.fromEntries(TYPES.map((r) => [r, s.commissionDefaults[r] != null ? String(s.commissionDefaults[r]! / 100) : ""]));

export function CommissionsPanel({ tenantId, settings, say }: { tenantId: string; settings: SalesSettings; say: (s: string) => void }) {
  const [basis, setBasis] = useState(settings.commissionBasis);
  const [when, setWhen] = useState(settings.commissionWhen);
  const [defaults, setDefaults] = useState<Record<string, string>>(() => drafts(settings));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBasis(settings.commissionBasis);
    setWhen(settings.commissionWhen);
    setDefaults(drafts(settings));
  }, [settings]);

  async function save() {
    setBusy(true);
    say("");
    try {
      const commissionDefaults = Object.fromEntries(
        TYPES.map((r) => {
          const n = parseFloat(defaults[r] ?? "");
          return [r, Number.isFinite(n) ? Math.max(0, Math.min(10000, Math.round(n * 100))) : null];
        }),
      );
      await call("setSalesSettings", { tenantId, commissionBasis: basis, commissionWhen: when, commissionDefaults });
      say("Commission plan saved.");
    } catch (err) {
      say((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-3">
      <CardTitle>Commission plan</CardTitle>
      <p className="text-xs text-steel">
        Your company's rule. Each employee type gets a default rate; to give one person their own, open them under People (that
        wins, and 0 means none). The estimate's owner earns it, and it follows the estimate onto the invoice. Reports →
        Commissions adds it up for any period; Payroll includes it.
      </p>
      <div className="flex flex-wrap gap-4">
        <div>
          <Label>Figured on</Label>
          <Select className="w-72" value={basis} onChange={(e) => setBasis(e.target.value as typeof basis)}>
            {(Object.keys(COMMISSION_BASIS_LABEL) as Array<keyof typeof COMMISSION_BASIS_LABEL>).map((k) => (
              <option key={k} value={k}>
                {COMMISSION_BASIS_LABEL[k].replace(/^./, (c) => c.toUpperCase())}
              </option>
            ))}
          </Select>
          {basis === "parts" ? <p className="mt-0.5 text-[11px] text-steel">A part is a line that carries a cost; labor, service calls and fees don't.</p> : null}
        </div>
        <div>
          <Label>Earned</Label>
          <Select className="w-64" value={when} onChange={(e) => setWhen(e.target.value as typeof when)}>
            <option value="collected">As the customer pays (deposit, then balance)</option>
            <option value="invoiced">When the invoice is sent</option>
          </Select>
        </div>
      </div>
      <div>
        <Label>Default rate by employee type</Label>
        <div className="mt-1 grid max-w-md gap-1">
          {TYPES.map((r) => (
            <label key={r} className="flex items-center justify-between gap-3 text-sm text-ink">
              <span>{ROLE_LABEL[r]}</span>
              <span className="flex items-center gap-1">
                <Input type="number" step="0.25" min="0" max="100" className="w-24 py-1 text-right text-sm" placeholder="None" value={defaults[r] ?? ""} onChange={(e) => setDefaults({ ...defaults, [r]: e.target.value })} />
                <span className="w-4 text-xs text-steel">%</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <Button busy={busy} onClick={() => void save()}>
        Save commission plan
      </Button>
    </Card>
  );
}
